<template>
  <section
    class="overflow-hidden select-none relative"
    :class="size === 'small' ? 'size-[30px]' : 'size-[var(--currentSize)]'"
    :style="`--currentSize: ${currentSize}`"
  >
    <svg
      aria-hidden="true"
      :class="size === 'small' ? 'origin-top-left' : 'size-full'"
      :height="SIZES.DEFAULT"
      preserveAspectRatio="xMidYMid meet"
      :style="
        size === 'small'
          ? `transform: scale(${SIZES.SMALL / SIZES.DEFAULT});`
          : undefined
      "
      :viewBox="`0 0 ${SIZES.DEFAULT} ${SIZES.DEFAULT}`"
      :width="SIZES.DEFAULT"
    >
      <defs>
        <path
          :id="`base-shape-path-${id}`"
          d="M36.3127 12.986C39.9599 1.57544 54.103 -2.2142 62.9668 5.84412C65.31 7.97434 68.8109 8.16508 71.3552 6.30113C80.9797 -0.749906 94.6649 4.55588 97.1463 16.3004C97.8022 19.4051 100.53 21.6293 103.683 21.6298C115.609 21.6316 123.609 34.0062 118.76 44.9509C117.478 47.8441 118.391 51.2523 120.948 53.1171C130.619 60.171 129.878 74.8877 119.551 80.8521C116.821 82.4288 115.571 85.7191 116.555 88.7358C120.279 100.148 111.08 111.585 99.2192 110.291C96.0838 109.949 93.1473 111.865 92.1831 114.881C88.536 126.292 74.3929 130.081 65.5291 122.023C63.1859 119.893 59.685 119.702 57.1407 121.566C47.5162 128.617 33.831 123.311 31.3496 111.567C30.6937 108.462 27.9655 106.238 24.813 106.237C12.8873 106.235 4.88733 93.8608 9.73621 82.9161C11.018 80.0229 10.1048 76.6146 7.54809 74.7499C-2.12351 67.696 -1.38258 52.9793 8.94443 47.0149C11.6744 45.4382 12.9249 42.1479 11.9406 39.1312C8.21728 27.7194 17.4161 16.2819 29.2767 17.576C32.4121 17.9181 35.3486 16.0025 36.3127 12.986Z"
        />
        <clipPath :id="`clip-bg-${id}`" clipPathUnits="userSpaceOnUse">
          <use :href="`#base-shape-path-${id}`" />
        </clipPath>
      </defs>

      <g :clip-path="`url(#clip-bg-${id})`">
        <foreignObject
          :height="SIZES.DEFAULT"
          :width="SIZES.DEFAULT"
          x="0"
          y="0"
        >
          <div
            ref="holo"
            class="bg-fixed size-full bg-center bg-repeat [background-image:var(--bgImg)] [background-size:1536px_1024px] motion-reduce:[background-attachment:unset] md:[background-position:0_-100px]"
            :style="{
              '--bgImg': `url(${imagePath}), radial-gradient(circle at 50% 50%, #F4DEEF 0%, #E8D4F0 20%, #D9E7F7 35%, #E6F3F5 50%, #F8E6DC 65%, #D1F3ED 80%, #F5E8DC 100%)`,
            }"
          />
        </foreignObject>
      </g>

      <g>
        <foreignObject
          :height="SIZES.DEFAULT"
          :width="SIZES.DEFAULT"
          x="0"
          y="0"
        >
          <div class="flex size-full items-center justify-center">
            <IconCheckInCircle :height="40" :width="40" />
          </div>
        </foreignObject>
      </g>

      <!--
        SSR fallback text. Visible immediately from server-rendered HTML, then
        hidden once the canvas overlay finishes painting. The fixed font size
        avoids all the per-browser and per-locale hacks the old `textPath`
        implementation accumulated: no `textLength`, no `lengthAdjust`, no
        Safari tracking, no FR/PT country tweaks. The canvas takes over for
        precise fitting and fill-tracking on hydration.
      -->
      <g v-show="!isCanvasReady">
        <!-- Reversed path for Firefox: `textPath` renders on the opposite side
             of the path in Firefox, so the path is drawn right-to-left and
             `side="right"` corrects the baseline orientation. -->
        <path
          :id="`top-text-path-${id}`"
          :d="
            isFirefox
              ? 'M98.4707,64A34.4707,34.4707 0 0 029.5293,64'
              : 'M29.5293,64a34.4707,34.4707 0 0,1 68.9414,0'
          "
          fill="none"
          transform="rotate(-15 64 64)"
        />
        <text
          :key="`top-text-${id}-${isFirefox}`"
          fill="currentColor"
          :style="fallbackTextStyle"
          text-anchor="middle"
        >
          <textPath
            :href="`#top-text-path-${id}`"
            :side="isFirefox ? 'right' : 'left'"
            startOffset="50%"
          >
            {{ topText }}
          </textPath>
        </text>
        <path
          :id="`bottom-text-path-${id}`"
          d="M20.58 75.63a44.95 44.95 0 1 0 86.84-23.26 44.95 44.95 0 1 0-86.84 23.26"
          fill="none"
        />
        <text
          fill="currentColor"
          :style="fallbackTextStyle"
          text-anchor="middle"
        >
          <textPath :href="`#bottom-text-path-${id}`" startOffset="71">
            {{ bottomText }}
          </textPath>
        </text>
      </g>
    </svg>

    <!--
      Arc text is rendered on a transparent Canvas overlay (deterministic across
      browsers) instead of SVG `<textPath>`, which required per-browser hacks.
      The canvas draws in the same 0..128 user space as the SVG viewBox and is
      CSS-sized to fill the section, so the existing `transform: scale` for the
      `small` variant applies automatically.
    -->
    <canvas
      ref="arcCanvas"
      class="absolute inset-0 size-full pointer-events-none"
    />
  </section>
</template>

<script setup lang="ts">
import { toPublicPath } from '#imports'
import { computed, useId, useTemplateRef } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { isBrowser } from '@backmarket/utils/env/isBrowser'
import { IconCheckInCircle } from '@ds/icons/IconCheckInCircle'

import { useScrollPinnedBackground } from '~/scopes/product-core/composables/useScrollPinnedBackground/useScrollPinnedBackground'

import {
  HOLO_HEIGHT,
  HOLO_IMAGE_PATH,
  HOLO_WIDTH,
  MD_BREAKPOINT,
  SIZES,
} from './Sticker.constants'
import translations from './Sticker.translations'
import {
  buildArcTextOptions,
  useArcText,
} from './composables/useArcText/useArcText'

const props = withDefaults(
  defineProps<{
    size?: 'default' | 'small'
  }>(),
  {
    size: 'default',
  },
)

const id = useId()

const i18n = useI18n()
const locale = useI18nLocale()

const isFirefox = computed(() => {
  return isBrowser() && navigator.userAgent.includes('Firefox')
})

const currentSize = computed(() => {
  return props.size === 'small' ? SIZES.SMALL : SIZES.DEFAULT
})

const imagePath = toPublicPath(HOLO_IMAGE_PATH)

// `background-attachment: fixed` is unreliable here in every engine, not just
// WebKit: iOS doesn't support it at all, and on GalleryMobile the holo div sits
// inside the horizontally scrollable image carousel (`overflow-x-auto`), which
// makes Chrome/Firefox drop the background too — same failure mode as the
// clipped `foreignObject` issue WebKit has always had. So everywhere except
// reduced-motion users we disable native `bg-fixed` and re-create the parallax
// in JS by pinning the background to the viewport on scroll — mirroring the
// approach used in VerifiedRefurbishedCarousel.
const prefersReducedMotion = () =>
  isBrowser() && window.matchMedia('(prefers-reduced-motion: reduce)').matches

const useJsParallax = computed(() => !prefersReducedMotion())

const holoRef = useTemplateRef<HTMLElement>('holo')

function paintParallax() {
  const el = holoRef.value
  if (!el) return

  const rect = el.getBoundingClientRect()
  const isMd = window.innerWidth >= MD_BREAKPOINT
  // Where `bg-fixed` anchors the image within the viewport: `0 -100px` at `md+`,
  // `center` below it (matching `bg-center`).
  const anchorX = isMd ? 0 : (window.innerWidth - HOLO_WIDTH) / 2
  const anchorY = isMd ? -100 : (window.innerHeight - HOLO_HEIGHT) / 2

  el.style.backgroundAttachment = 'scroll'
  el.style.backgroundPosition = `${anchorX - rect.left}px ${anchorY - rect.top}px`
}

useScrollPinnedBackground(useJsParallax, paintParallax)

const topText = computed(() => i18n(translations.topText))
const bottomText = computed(() => i18n(translations.bottomText))

// Fixed font for the SSR `<textPath>` fallback. Small enough to fit most
// localized strings without `textLength` hacks; the canvas overlay replaces
// it with precise fitting on hydration.
const fallbackTextStyle = {
  fontFamily: '"BMDupletTXT", Helvetica, sans-serif',
  fontWeight: 700,
  fontSize: '11px',
}

const arcTextOptions = computed(() =>
  buildArcTextOptions(topText.value, bottomText.value),
)

const arcCanvas = useTemplateRef<HTMLCanvasElement>('arcCanvas')

const { isCanvasReady } = useArcText(arcCanvas, {
  arcs: arcTextOptions,
  locale: () => locale,
})
</script>
